import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  Divider,
  Sheet,
  Stack,
  Table,
  Typography
} from '@mui/joy'
import {
  MenuBook as MenuBookIcon,
  Key as KeyIcon,
  Send as SendIcon,
  Warning as WarningIcon,
  CheckCircle as CheckCircleIcon
} from '@mui/icons-material'

const API_BASE = import.meta.env.VITE_API_URL || 'https://tu-servidor'

const CodeBlock = ({ children }: { children: string }) => (
  <Sheet
    variant="soft"
    sx={{
      p: 2,
      borderRadius: 'sm',
      bgcolor: 'background.level1',
      overflowX: 'auto'
    }}
  >
    <Typography
      component="pre"
      level="body-xs"
      sx={{
        fontFamily: 'monospace',
        whiteSpace: 'pre',
        margin: 0
      }}
    >
      {children}
    </Typography>
  </Sheet>
)

export default function ApiMessageGuide() {
  return (
    <Stack spacing={3}>
      <Alert color="primary" variant="soft" startDecorator={<MenuBookIcon />}>
        <Box>
          <Typography level="title-sm">Guia de la API de mensajes</Typography>
          <Typography level="body-xs">
            Esta guia cubre el envio de mensajes de texto. Las plantillas de
            Meta usan otro endpoint distinto (/send-template) y no se ven
            afectadas por estos ajustes.
          </Typography>
        </Box>
      </Alert>

      {/* ===== Paso 1: token ===== */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography level="title-md" startDecorator={<KeyIcon />}>
              1. Consigue el token de tu conexion
            </Typography>
            <Typography level="body-sm">
              Cada conexion de WhatsApp tiene su propio token. Lo encuentras en{' '}
              <strong>Conexiones → (tu conexion) → Credenciales</strong>. Ese
              token identifica la conexion, por eso{' '}
              <strong>no hace falta indicar a que numero envias desde</strong>:
              se deduce del token.
            </Typography>
            <Alert color="warning" variant="soft" startDecorator={<WarningIcon />}>
              <Typography level="body-xs">
                Trata el token como una contrasena. Quien lo tenga puede enviar
                mensajes en tu nombre. Si se filtra, regeneralo desde la misma
                pantalla.
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>

      {/* ===== Paso 2: enviar ===== */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography level="title-md" startDecorator={<SendIcon />}>
              2. Envia un mensaje
            </Typography>

            <Typography level="body-sm">
              <strong>Endpoint:</strong>{' '}
              <Chip size="sm" variant="soft">
                POST {API_BASE}/api/messages/send
              </Chip>
            </Typography>

            <CodeBlock>{`curl -X POST ${API_BASE}/api/messages/send \\
  -H "Authorization: Bearer TU_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "number": "593987654321",
    "body": "Juan, registraste 12 minutos de retraso el 20/07. Todo bien?"
  }'`}</CodeBlock>

            <Divider />

            <Typography level="title-sm">Campos principales</Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflowX: 'auto' }}>
              <Table size="sm" stickyHeader>
                <thead>
                  <tr>
                    <th style={{ width: '22%' }}>Campo</th>
                    <th style={{ width: '14%' }}>Tipo</th>
                    <th>Descripcion</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <code>number</code>
                    </td>
                    <td>string</td>
                    <td>
                      Obligatorio. Solo digitos, con codigo de pais y sin signos
                      ni espacios.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>body</code>
                    </td>
                    <td>string</td>
                    <td>
                      Texto del mensaje. Si la IA esta activada, este es el
                      texto que se reescribe.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>userId</code>
                    </td>
                    <td>number</td>
                    <td>Opcional. Asigna el ticket a un usuario.</td>
                  </tr>
                  <tr>
                    <td>
                      <code>queueId</code>
                    </td>
                    <td>number</td>
                    <td>Opcional. Asigna el ticket a una cola.</td>
                  </tr>
                  <tr>
                    <td>
                      <code>sendSignature</code>
                    </td>
                    <td>boolean</td>
                    <td>
                      Opcional. Antepone el nombre del usuario. Se aplica
                      despues de la reescritura con IA.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <code>closeTicket</code>
                    </td>
                    <td>boolean</td>
                    <td>Opcional. Cierra el ticket tras enviar.</td>
                  </tr>
                </tbody>
              </Table>
            </Sheet>
          </Stack>
        </CardContent>
      </Card>

      {/* ===== Paso 3: respuestas ===== */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography level="title-md" startDecorator={<CheckCircleIcon />}>
              3. Interpreta la respuesta
            </Typography>

            <Alert color="warning" variant="soft" startDecorator={<WarningIcon />}>
              <Box>
                <Typography level="title-sm">
                  La respuesta cambia si activas el intervalo aleatorio
                </Typography>
                <Typography level="body-xs">
                  Es el unico cambio de contrato de toda esta funcionalidad. Si
                  no activas el intervalo, tu integracion sigue funcionando
                  exactamente igual que antes.
                </Typography>
              </Box>
            </Alert>

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Sin intervalo — envio inmediato (comportamiento de siempre)
              </Typography>
              <CodeBlock>{`HTTP 200
{
  "status": "SUCCESS"
}`}</CodeBlock>
            </Box>

            <Box>
              <Typography level="title-sm" sx={{ mb: 1 }}>
                Con intervalo activado — envio encolado
              </Typography>
              <CodeBlock>{`HTTP 202
{
  "status": "QUEUED",
  "queued": true,
  "jobId": "1042",
  "scheduledFor": "2026-07-20T15:23:41.000Z",
  "delaySeconds": 187,
  "message": "Mensaje encolado. Se enviara respetando el intervalo aleatorio configurado."
}`}</CodeBlock>
              <Typography level="body-xs" sx={{ mt: 1, color: 'text.tertiary' }}>
                El mensaje aun no se ha enviado. Se enviara en{' '}
                <code>scheduledFor</code>. Trata el 202 como exito.
              </Typography>
            </Box>

            <Divider />

            <Typography level="title-sm">Errores comunes</Typography>
            <Sheet variant="outlined" sx={{ borderRadius: 'sm', overflowX: 'auto' }}>
              <Table size="sm">
                <thead>
                  <tr>
                    <th style={{ width: '15%' }}>Codigo</th>
                    <th>Que significa</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>
                      <Chip size="sm" color="danger" variant="soft">
                        401
                      </Chip>
                    </td>
                    <td>Token ausente, mal formado o inexistente.</td>
                  </tr>
                  <tr>
                    <td>
                      <Chip size="sm" color="danger" variant="soft">
                        400
                      </Chip>
                    </td>
                    <td>
                      Falta <code>number</code> o <code>body</code>, o el numero
                      tiene un formato invalido.
                    </td>
                  </tr>
                  <tr>
                    <td>
                      <Chip size="sm" color="danger" variant="soft">
                        503
                      </Chip>
                    </td>
                    <td>
                      La conexion de WhatsApp no esta conectada. Revisa su
                      estado en Conexiones.
                    </td>
                  </tr>
                </tbody>
              </Table>
            </Sheet>
          </Stack>
        </CardContent>
      </Card>

      {/* ===== Buenas practicas ===== */}
      <Card variant="outlined">
        <CardContent>
          <Stack spacing={2}>
            <Typography level="title-md">
              Buenas practicas para no arriesgar el numero
            </Typography>
            <Typography level="body-sm">
              WhatsApp detecta patrones automatizados. Estas medidas reducen el
              riesgo de bloqueo, en orden de impacto:
            </Typography>
            <Stack spacing={1} component="ul" sx={{ pl: 2, m: 0 }}>
              <Typography level="body-sm" component="li">
                <strong>Consigue que te respondan.</strong> Un mensaje que
                genera respuesta es la senal de legitimidad mas fuerte. Por eso
                la plantilla de IA sugerida siempre termina con una pregunta.
              </Typography>
              <Typography level="body-sm" component="li">
                <strong>Elimina la regularidad.</strong> Activa el intervalo
                aleatorio. La cadencia perfecta es mas sospechosa que la
                velocidad.
              </Typography>
              <Typography level="body-sm" component="li">
                <strong>Varia el contenido.</strong> Activa la reescritura con
                IA: el texto identico repetido es huella de spam.
              </Typography>
              <Typography level="body-sm" component="li">
                <strong>Simula comportamiento humano.</strong> Deja activado el
                indicador de escritura.
              </Typography>
              <Typography level="body-sm" component="li">
                <strong>Calienta los numeros nuevos.</strong> Empieza con pocos
                envios diarios y sube el volumen de forma gradual.
              </Typography>
            </Stack>
            <Alert color="neutral" variant="soft">
              <Typography level="body-xs">
                Para volumenes altos y sostenidos, la opcion de fondo mas segura
                es migrar la conexion a la API oficial de WhatsApp Cloud, que
                elimina el riesgo de bloqueo por automatizacion.
              </Typography>
            </Alert>
          </Stack>
        </CardContent>
      </Card>
    </Stack>
  )
}
